import { useToast } from "@/components/ui/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { Message, ChatHistory } from '@/types/chat';
import { Json } from '@/integrations/supabase/types';
import { UserType } from '@/types/user';

export const useChatHistory = () => {
  const { toast } = useToast();

  // Function to get chat history retention period based on user type
  const getHistoryRetentionPeriod = (userType: UserType): number => {
    switch (userType) {
      case 'free':
        return 1; // 24 hours (1 day)
      case 'starter':
        return 30; // 30 days
      case 'pro':
      case 'premium':
      case 'admin':
        return 365; // 365 days (effectively unlimited)
      default:
        return 1; // Default to 24 hours
    }
  };

  const loadChatHistory = async (chatId: string): Promise<Message[]> => {
    try {
      const { data: chatData, error } = await supabase
        .from('chat_history')
        .select('messages')
        .eq('id', chatId)
        .maybeSingle();

      if (error) {
        throw error;
      }

      if (chatData?.messages) {
        // Update last accessed timestamp
        await supabase
          .from('chat_history')
          .update({ last_accessed: new Date().toISOString() })
          .eq('id', chatId);

        return chatData.messages as Message[];
      }
      return [];
    } catch (error) {
      console.error('Error loading chat history:', error);
      toast({
        title: "Error",
        description: "Failed to load chat history. Please try again.",
        variant: "destructive",
      });
      return [];
    }
  };

  const saveChatHistory = async (
    userId: string, 
    title: string, 
    messages: Message[], 
    existingChatId?: string | null
  ): Promise<string | null> => {
    try {
      // Get user type to determine history retention
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('user_type')
        .eq('id', userId)
        .single();
      
      if (profileError) {
        console.error('Error getting user profile:', profileError);
        throw profileError;
      }
      
      const userType = profileData?.user_type as UserType;
      
      // Extract title from first user message if possible
      const firstUserMessage = messages.find(m => m.role === 'user')?.content || '';
      const chatTitle = title || firstUserMessage;

      // Create an object that matches the required Supabase schema
      const chatHistory = {
        user_id: userId,
        title: chatTitle.length > 50 ? chatTitle.substring(0, 47) + '...' : chatTitle,
        messages: messages as Json,
        last_accessed: new Date().toISOString()
      };

      let data;
      
      if (existingChatId) {
        // Update existing chat history
        const { data: updateData, error: updateError } = await supabase
          .from('chat_history')
          .update(chatHistory)
          .eq('id', existingChatId)
          .eq('user_id', userId) // Ensure the user owns this chat
          .select('id')
          .single();
        
        if (updateError) {
          console.error('Error updating chat history:', updateError);
          toast({
            title: "Warning",
            description: "Failed to update chat history.",
            variant: "destructive",
          });
          return null;
        }
        
        data = updateData;
      } else {
        // Create new chat history
        const { data: insertData, error: insertError } = await supabase
          .from('chat_history')
          .insert(chatHistory)
          .select('id')
          .single();
        
        if (insertError) {
          console.error('Error storing chat history:', insertError);
          toast({
            title: "Warning",
            description: "Failed to save chat history.",
            variant: "destructive",
          });
          return null;
        }
        
        data = insertData;
      }

      // Clean up old chat history based on user type's retention period
      await cleanupOldChatHistory(userId, userType);

      return data.id;
    } catch (error) {
      console.error('Chat history error:', error);
      return null;
    }
  };
  
  // Function to clean up old chat history based on user type
  const cleanupOldChatHistory = async (userId: string, userType: UserType): Promise<void> => {
    try {
      const retentionDays = getHistoryRetentionPeriod(userType);
      
      // Calculate the cutoff date based on retention period
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - retentionDays);
      const cutoffDateString = cutoffDate.toISOString();
      
      console.log(`Cleaning up chat history older than ${cutoffDateString} for user ${userId}`);
      
      // Delete chat history older than the retention period
      const { error } = await supabase
        .from('chat_history')
        .delete()
        .eq('user_id', userId)
        .lt('last_accessed', cutoffDateString);
      
      if (error) {
        console.error('Error cleaning up old chat history:', error);
      }
    } catch (error) {
      console.error('Error in chat history cleanup:', error);
    }
  };

  return { loadChatHistory, saveChatHistory };
};
